import React, { FC, useCallback, useState } from 'react'
import { Avatar, Box, Chip, CircularProgress, IconButton, Tooltip, Typography, keyframes, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ContentCopy, Check, PictureAsPdf } from '@mui/icons-material'
import ReactMarkdown, { Components } from 'react-markdown'
import { CleoMessage } from '../../../../models/cleo.model'
import ToolCallIndicator from './ToolCallIndicator'
import ThinkingIndicator from './ThinkingIndicator'
import MessageFeedback from './MessageFeedback'
import { getPdfByConsultationId } from '../../../../services/decisionFlow'
import { redirectToPDF } from '../../../../utils/pdf.util'
import { handlePromise } from '../../../../utils/handlePromisse'

type MessageBubbleProps = {
    message: CleoMessage
    onAction?: (text: string) => void
}

const CONSULTATION_LINK_RE = /^#consultation:(\d+):(\w+)$/
const PDF_LINK_RE = /^#pdf:(\d+):(\w+)$/

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`

const MessageBubble: FC<MessageBubbleProps> = ({ message, onAction }) => {
    const theme = useTheme()
    const isUser = message.role === 'user'
    const [pdfLoading, setPdfLoading] = useState<string | null>(null)
    const [pdfError, setPdfError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(message.content)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [message.content])

    const handlePdfDownload = useCallback(async (id: string, type: string) => {
        const key = `${id}:${type}`
        setPdfLoading(key)
        setPdfError(null)
        const pdfType = type === 'TOPIC' ? 'topic' : 'oral'
        const [result, error] = await handlePromise(
            getPdfByConsultationId({ consults: [{ consultationId: Number(id), type: pdfType }] }, false)
        )
        if (!error && result?.data?.pdf) {
            redirectToPDF(result.data.pdf)
        } else {
            setPdfError(key)
            setTimeout(() => setPdfError(null), 3000)
        }
        setPdfLoading(null)
    }, [])

    const markdownComponents: Components = {
        a: ({ href, children }) => {
            const consultMatch = href ? CONSULTATION_LINK_RE.exec(href) : null
            if (consultMatch && onAction) {
                const [, id, type] = consultMatch
                const typeLabel = type === 'TOPIC' ? 'Tópica' : 'Oral'
                return (
                    <Chip
                        label={children}
                        variant="outlined"
                        size="small"
                        onClick={() => onAction(`Detalhe a consulta ${id} do tipo ${typeLabel}`)}
                        sx={{
                            cursor: 'pointer',
                            borderColor: 'primary.main',
                            color: 'secondary.main',
                            my: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: 'primary.lighter',
                                transform: 'translateY(-1px)',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                        }}
                    />
                )
            }

            const pdfMatch = href ? PDF_LINK_RE.exec(href) : null
            if (pdfMatch) {
                const [, id, type] = pdfMatch
                const key = `${id}:${type}`
                const isLoading = pdfLoading === key
                const hasError = pdfError === key
                return (
                    <Chip
                        icon={isLoading ? <CircularProgress size={14} /> : <PictureAsPdf fontSize="small" />}
                        label={hasError ? 'Erro ao gerar PDF' : children}
                        variant="outlined"
                        size="small"
                        disabled={isLoading}
                        onClick={() => handlePdfDownload(id, type)}
                        sx={{
                            cursor: 'pointer',
                            borderColor: hasError ? 'error.main' : 'success.main',
                            color: hasError ? 'error.dark' : 'success.dark',
                            my: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: hasError ? 'error.lighter' : 'success.lighter',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    />
                )
            }

            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
        },
    }

    if (isUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, animation: `${fadeIn} 0.3s ease` }}>
                <Box
                    sx={{
                        maxWidth: '75%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        px: 2.5,
                        py: 1.5,
                        borderRadius: '20px 20px 4px 20px',
                        wordBreak: 'break-word',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    }}
                >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {message.content}
                    </Typography>
                </Box>
            </Box>
        )
    }

    const showThinking = message.isStreaming && !message.content && !message.isToolProcessing
    const showContent = !!message.content

    return (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start', animation: `${fadeIn} 0.3s ease` }}>
            <Avatar
                sx={{
                    width: 34,
                    height: 34,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                    color: theme.palette.primary.contrastText,
                    fontSize: 14,
                    fontWeight: 700,
                    flexShrink: 0,
                    mt: 0.5,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
            >
                C
            </Avatar>
            <Box sx={{ maxWidth: '80%', minWidth: 120 }}>
                {message.isToolProcessing && message.toolCalls && (
                    <ToolCallIndicator tools={message.toolCalls} />
                )}

                {showThinking && <ThinkingIndicator />}

                {showContent && (
                    <Box
                        sx={{
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            px: 2.5,
                            py: 2,
                            borderRadius: '4px 20px 20px 20px',
                            wordBreak: 'break-word',
                            position: 'relative',
                            '&:hover .copy-btn': { opacity: 1 },
                            '& p': { m: 0, mb: 1, lineHeight: 1.7 },
                            '& p:last-child': { mb: 0 },
                            '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
                            '& li': { mb: 0.5, lineHeight: 1.6 },
                            '& code': {
                                bgcolor: 'action.hover',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                fontSize: '0.85em',
                                fontFamily: 'monospace',
                            },
                            '& pre': {
                                bgcolor: 'grey.900',
                                color: 'grey.100',
                                p: 2,
                                borderRadius: 2,
                                overflow: 'auto',
                                mb: 1,
                            },
                            '& strong': { fontWeight: 600, color: 'text.primary' },
                            '& h1, & h2, & h3, & h4': {
                                mt: 1.5,
                                mb: 0.75,
                                fontWeight: 600,
                                color: 'text.primary',
                            },
                            '& table': {
                                borderCollapse: 'collapse',
                                width: '100%',
                                mb: 1,
                            },
                            '& th, & td': {
                                border: '1px solid',
                                borderColor: 'divider',
                                px: 1.5,
                                py: 0.75,
                                fontSize: '0.875rem',
                            },
                            '& th': {
                                bgcolor: 'action.hover',
                                fontWeight: 600,
                            },
                        }}
                    >
                        <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>

                        {!message.isStreaming && message.content.length > 20 && (
                            <Tooltip title={copied ? 'Copiado!' : 'Copiar'} arrow placement="top">
                                <IconButton
                                    className="copy-btn"
                                    size="small"
                                    onClick={handleCopy}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        opacity: 0,
                                        transition: 'opacity 0.2s ease',
                                        bgcolor: 'background.paper',
                                        boxShadow: 1,
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    {copied
                                        ? <Check sx={{ fontSize: 14, color: 'success.main' }} />
                                        : <ContentCopy sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    }
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {message.isStreaming && showContent && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            width: 2,
                            height: 18,
                            bgcolor: 'primary.main',
                            ml: 0.5,
                            mt: 0.5,
                            borderRadius: 1,
                            animation: 'blink 0.8s ease-in-out infinite',
                            '@keyframes blink': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0 },
                            },
                        }}
                    />
                )}

                {showContent && !message.isStreaming && message.id != null && (
                    <MessageFeedback messageId={message.id} initialRating={message.feedbackRating} />
                )}
            </Box>
        </Box>
    )
}

export default MessageBubble